import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast } from "react-toastify";
// Constants - Functions
import { WEB_SERVICE_MIMS, errorColor } from "../../../../../../config/constants";
// Components
import {
  MainDiv,
  Header,
  DiagnosticImgContainer,
  DiagnosticImg,
  DiagnosticTitle,
  SeparatorDiv,
  PrescriptionDaysDiv,
  PrescriptionDaysAligner,
  InputCheckboxDiv,
  DayText,
  DiagnosticPrescriptionInfo,
  OpenButton,
  OpenButtonImg,
  Body,
  DiagnosticProtocolsInfo,
  DiagnosticDatesAligner,
  DiagnosticDatesDiv,
  DiagnosticTitleDiv,
  DiagnosticDateDiv,
  DiagnosticDateText,
  StatusDiv,
  StatusText,
  StatusImg,
  OpenPopupButton,
  OpenPopupImg,
  PopupDiv,
  DiagnosticPopupTitle,
  DiagnosticDatePopupDiv,
  InputDateDiv,
  InputDate,
  ActionsDiv,
  CancelButton,
  SaveButton,
} from "./styles";
import Popup from "../../../../../popup";
import UseFetch from "../../../../../../config/useFetch";
// Assets
import DiagnosticIco from "../../../../../../assets/icons/ico_diagnostics.svg";
import ArrowUpImg from "../../../../../../assets/icons/ico_arrow_up_blue.svg";
import ArrowDownImg from "../../../../../../assets/icons/ico_arrow_down_blue.svg";
import CheckIco from "../../../../../../assets/icons/check_white.svg";
import WarningIco from "../../../../../../assets/icons/ico_warning_white.svg";
import HourglassIco from "../../../../../../assets/icons/hourglass.svg";
import EditBlueIco from "../../../../../../assets/icons/btn_edit_user_blue.svg";
import EditGrayIco from "../../../../../../assets/icons/btn_edit_user_gray.svg";

const DiagnosticAssessments = ({
  startingDate,
  prescribedDayThisWeek,
  diagnosticDates,
  newDiagnosticDate,
  setNewDiagnosticDate,
  diagnosticNumber,
  setDiagnosticNumber,
  diagnosticId,
  setDiagnosticId,
  showDiagnosticDatePopup,
  setShowDiagnosticDatePopup,
  refreshDiagnosticDates,
  setRefreshDiagnosticDates,
}) => {
  const [newDiagnosticDateError, setNewDiagnosticDateError] = useState(false);
  const [showDiagnosticDates, setShowDiagnosticDates] = useState(false);
  const [TitleTexts] = useState(["First", "Second", "First Extra", "Second Extra"]);
  const [t] = useTranslation("global");
  const { patientId } = useParams();

  // Show diagnostic dates component if there is at least one incomplete status
  useEffect(() => {
    const notCompletedDiagnosticDates = diagnosticDates.filter((diagnosticDate) => diagnosticDate.STATUS === "NOT_COMPLETED");
    if (notCompletedDiagnosticDates.length) setShowDiagnosticDates(true);

    return () => {
      setShowDiagnosticDates(false);
    };
  }, [diagnosticDates]);

  const renderPrescriptionDays = () => {
    const days = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    return (
      <PrescriptionDaysDiv>
        <PrescriptionDaysAligner>
          {days.map((day) => {
            const activeDay = day === prescribedDayThisWeek;

            const getBorderRadius = () => {
              if (day === "MONDAY") return "30px 0 0 30px";
              if (day === "SUNDAY") return "0 30px 30px 0";
              return "0";
            };

            return (
              <InputCheckboxDiv key={day} borderRadius={getBorderRadius()} background={activeDay ? "#F15A24" : "#E6E6E6"}>
                <DayText color={activeDay ? "#FFF" : "#808080"}>{t(`prescriptions_tableProtocolDays_${day.toLowerCase()}`)}</DayText>
              </InputCheckboxDiv>
            );
          })}
        </PrescriptionDaysAligner>
      </PrescriptionDaysDiv>
    );
  };

  const renderDiagnosticDates = () => {
    return diagnosticDates.map((diagnosticDate, i) => {
      const DIAGNOSTIC_DATE = diagnosticDate.DIAGNOSTIC_DATE;
      const getStatusInfo = () => {
        if (diagnosticDate.STATUS === "COMPLETED") {
          return {
            background: "#22B573",
            img: CheckIco,
            text: "COMPLETED",
          };
        }
        if (diagnosticDate.STATUS === "NOT_COMPLETED_GRAY") {
          return {
            background: "#C8C8C8",
            img: WarningIco,
            text: "NOT COMPLETED",
          };
        }
        if (diagnosticDate.STATUS === "NOT_COMPLETED") {
          return {
            background: "#F15A24",
            img: WarningIco,
            text: "NOT COMPLETED",
          };
        }
        return {
          background: "#FDCD56",
          img: HourglassIco,
          text: "PRESCRIBED",
        };
      };
      const { background, img, text } = getStatusInfo();
      const isClickable =
        moment(startingDate, "DD/MM/YYYY").add(12, "weeks").isAfter(moment(), "day") &&
        diagnosticDate.STATUS !== "COMPLETED" &&
        diagnosticDate.STATUS !== "NOT_COMPLETED_GRAY";
      const isNotCompleted = diagnosticDate.STATUS === "NOT_COMPLETED";

      const showMarkAsNotCompleted = DIAGNOSTIC_DATE && isNotCompleted;

      const handleSetPopupData = () => {
        setNewDiagnosticDate(DIAGNOSTIC_DATE ? new Date(moment(DIAGNOSTIC_DATE, "DD/MM/YYYY")) : "");
        setDiagnosticNumber(i);
        setDiagnosticId(diagnosticDate.RECSYS_DIAGNOSTIC_ID);
        setShowDiagnosticDatePopup(true);
      };

      return (
        <DiagnosticDatesAligner key={diagnosticDate.RECSYS_DIAGNOSTIC_ID}>
          <DiagnosticDatesDiv
            border={showMarkAsNotCompleted ? "1.5px solid #F15A24" : "none"}
            background={showMarkAsNotCompleted ? "rgba(241, 90, 36, 0.10)" : "#f3f3f3"}
          >
            <DiagnosticTitleDiv>
              <DiagnosticTitle>{TitleTexts[i]} Diagnostic</DiagnosticTitle>
            </DiagnosticTitleDiv>
            <DiagnosticDateDiv>
              <DiagnosticDateText>{DIAGNOSTIC_DATE}</DiagnosticDateText>
            </DiagnosticDateDiv>
            {DIAGNOSTIC_DATE && (
              <StatusDiv background={background}>
                <StatusText>{text}</StatusText>
                <StatusImg src={img} alt="status-ico" />
              </StatusDiv>
            )}
          </DiagnosticDatesDiv>
          <OpenPopupButton onClick={handleSetPopupData} clickable={isClickable}>
            <OpenPopupImg src={!isClickable ? EditGrayIco : EditBlueIco} alt="edit-ico" />
          </OpenPopupButton>
        </DiagnosticDatesAligner>
      );
    });
  };

  const renderSetDiagnosticDatePopup = () => {
    const getInitialDate = () => {
      if (!Number(diagnosticNumber)) return moment(startingDate, "DD/MM/YYYY");
      return moment(diagnosticDates[diagnosticNumber - 1].DIAGNOSTIC_DATE, "DD/MM/YYYY").add(7, "days");
    };

    const getFinalDate = () => {
      const lastWeekRoutineDate = moment(startingDate, "DD/MM/YYYY").add(11, "weeks");
      if (diagnosticDates.length === diagnosticNumber + 1) return lastWeekRoutineDate;
      const nextDiagnosticDate = diagnosticDates[diagnosticNumber + 1].DIAGNOSTIC_DATE;
      if (!nextDiagnosticDate) return lastWeekRoutineDate;
      return moment(nextDiagnosticDate, "DD/MM/YYYY").subtract(6, "days");
    };

    const validDates = (date) => {
      const initialDate = getInitialDate();
      const finalDate = getFinalDate();

      return (
        moment(date).isSameOrAfter(moment(), "day") &&
        moment(date).isSameOrAfter(initialDate, "day") &&
        moment(date).isBefore(finalDate, "day")
      );
    };

    const firstAvailableDate = () => {
      if (newDiagnosticDate) return newDiagnosticDate;
      const initialDate = moment(getInitialDate(), "DD/MM/YYYY");
      if (initialDate.isBefore(moment(), "day")) return new Date();
      return initialDate.toDate();
    };

    const handleSetDate = (date) => {
      setNewDiagnosticDate(date);
      setNewDiagnosticDateError(false);
    };

    const updateDiagnosticDate = () => {
      if (newDiagnosticDate) {
        UseFetch("put", `${WEB_SERVICE_MIMS}/recsys/update-diagnostic-date/${patientId}`, new AbortController(), {
          RECSYS_DIAGNOSTIC_ID: diagnosticId,
          newDiagnosticDate: moment(newDiagnosticDate).format("YYYY/MM/DD"),
        })
          .then(({ data }) => {
            if (data.diagnosticAndPrescriptionsUpdated) {
              toast.success("Diagnostic date saved successfully");
              setShowDiagnosticDatePopup(false);
              setRefreshDiagnosticDates(!refreshDiagnosticDates);
            }
          })
          .catch((error) => {
            console.log("Save diagnostic data: ", error);
          });
      }
    };

    return (
      <Popup isShowed={showDiagnosticDatePopup} setIsShowed={setShowDiagnosticDatePopup}>
        <PopupDiv>
          <DiagnosticPopupTitle>Set date</DiagnosticPopupTitle>
          <DiagnosticDatePopupDiv>
            <DiagnosticTitle>{TitleTexts[diagnosticNumber]} Diagnostic</DiagnosticTitle>
            <InputDateDiv>
              <InputDate
                selected={newDiagnosticDate}
                onChange={(date) => handleSetDate(date)}
                filterDate={validDates}
                openToDate={firstAvailableDate()}
                placeholderText="dd/mm/yyyy"
                dateFormat={"dd/MM/yyyy"}
                border={newDiagnosticDateError ? `1px solid ${errorColor}` : `1px solid #C8C8C8`}
              />
            </InputDateDiv>
          </DiagnosticDatePopupDiv>
          <ActionsDiv>
            <CancelButton onClick={() => setShowDiagnosticDatePopup(false)}>Cancel</CancelButton>
            <SaveButton onClick={updateDiagnosticDate}>Save</SaveButton>
          </ActionsDiv>
        </PopupDiv>
      </Popup>
    );
  };

  return (
    <>
      <MainDiv>
        <Header>
          <DiagnosticImgContainer>
            <DiagnosticImg src={DiagnosticIco} alt="diagnostic-ico" />
          </DiagnosticImgContainer>
          <DiagnosticTitle>RGS Diagnostics at Home</DiagnosticTitle>
          <SeparatorDiv />
          {prescribedDayThisWeek && renderPrescriptionDays()}
          <DiagnosticPrescriptionInfo>
            {prescribedDayThisWeek ? "There is a prescribed diagnosis this week" : "No prescribed diagnosis this week"}
          </DiagnosticPrescriptionInfo>
          <OpenButton onClick={() => setShowDiagnosticDates(!showDiagnosticDates)}>
            <OpenButtonImg src={showDiagnosticDates ? ArrowUpImg : ArrowDownImg} alt="arrow-ico" />
          </OpenButton>
        </Header>
        {showDiagnosticDates && (
          <Body>
            <DiagnosticProtocolsInfo>
              RGS+ diagnostic activities: Quality Control, Constellations, Vertical AR Circle, and Horizontal AR Circle.
            </DiagnosticProtocolsInfo>
            {renderDiagnosticDates()}
          </Body>
        )}
      </MainDiv>
      {showDiagnosticDatePopup && renderSetDiagnosticDatePopup()}
    </>
  );
};

export default DiagnosticAssessments;
